<template>
  <EChartsScrollContainer class="echarts-column-and-line" :minWidth="minWidth">
    <EChartsBase
      :isEmpty="getIsChartDataEmpty"
      :emptyMessage="emptyMessage"
      :height="getChartHeight"
      :titleConfig="titleConfig"
    >
      <EChartsChartZoom v-slot="{ withZoom, bindVChart }" :enabled="enableZoom" :xAxisIndex="getZoomXAxisIndex">
        <VChart
          ref="chartRef"
          :option="withZoom(getChartOptions)"
          :updateOptions="{ replaceMerge: ['legend', 'grid', 'series', 'yAxis'] }"
          autoresize
          class="chartBar"
          :class="{ 'chartBar--full': mirrorYAxis }"
          :style="{ height: `${getChartHeight}px` }"
          @finished="onChartFinished(bindVChart)"
          @legendselectchanged="onLegendSelectChanged"
        />
      </EChartsChartZoom>
    </EChartsBase>
  </EChartsScrollContainer>
</template>

<script>
  //TODO: Перевести на TS
  import { ref } from 'vue';
  import VChart from 'vue-echarts';
  import { BarChart, LineChart } from 'echarts/charts';
  import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components';
  import { use } from 'echarts/core';
  import { CanvasRenderer } from 'echarts/renderers';

  import text from '@/common/mixins/text';

  import chartConstants from '@/components/ChartComponents/chartConstants.js';

  import { ECHARTS_CHART_ZOOM_GRID_TOP, EChartsChartZoom } from './chartZoom';
  import { useAdaptiveGridBottom } from './composables/useAdaptiveGridBottom';
  import EChartsBase from './EChartsBase.vue';
  import EChartsScrollContainer from './EChartsScrollContainer.vue';
  import { buildCardAxisTooltipHtml, createTooltipPositioning, normalizeEChartsLabel } from './utils';

  use([GridComponent, LegendComponent, TooltipComponent, BarChart, LineChart, CanvasRenderer]);

  export default {
    name: 'EChartsColumnAndLine',
    components: {
      EChartsBase,
      EChartsScrollContainer,
      EChartsChartZoom,
      VChart,
    },
    mixins: [text],
    setup() {
      const chartRef = ref(null);
      const { gridBottom } = useAdaptiveGridBottom({
        chartRef,
        legendBottom: 8,
        fallbackBottom: '14%',
      });

      return {
        chartRef,
        gridBottom,
      };
    },
    props: {
      xData: {
        type: Array,
        default: () => [],
      },
      columnYData: {
        type: Array,
        default: () => [],
      },
      columnYName: {
        type: String,
        default: 'Количество выявленных барьеров',
      },
      lineYData: {
        type: Array,
        default: () => [],
      },
      lineYName: {
        type: String,
        default: 'Сумма ущерба, млрд долл. США',
      },
      lines: {
        type: Array,
        default: null,
      },
      columns: {
        type: Array,
        default: null,
      },
      layout: {
        type: String,
        default: 'split',
        validator: (value) => ['split', 'combined'].includes(value),
      },
      tooltipLabels: {
        type: Array,
        default: undefined,
      },
      columnDataLabelsText: {
        type: String,
        default: '',
      },
      lineDataLabelsText: {
        type: String,
        default: '',
      },

      lineYTooltipPrefix: {
        type: String,
      },

      colors: {
        type: Array,
        default: () => ['#17A1FA', '#73EA48'],
      },

      marker: {
        type: Object,
        default: () => ({
          size: 6,
          colors: [chartConstants.whiteColor],
          strokeColors: '#73EA48',
        }),
      },

      stroke: {
        type: Object,
        default: () => ({
          width: [0, 4],
        }),
      },

      sizeXYLabel: {
        type: Number,
        default: 15,
      },

      sizeDataLabel: {
        type: Number,
        default: 20,
      },

      offsetYDataLabel: {
        type: Number,
        default: -17,
      },

      height: {
        type: Number,
      },

      barMaxWidth: {
        type: Number,
        default: 48,
      },

      tickAmount: {
        type: Number,
        default: undefined,
      },

      stepSize: {
        type: Number,
        default: undefined,
      },

      tickAmountSecond: {
        type: Number,
        default: undefined,
      },

      stepSizeSecond: {
        type: Number,
        default: undefined,
      },

      formatYLabelFunction: {
        type: Function,
        default: null,
      },

      formatTooltipFunction: {
        type: Function,
        default: null,
      },

      plotOptions: {
        type: Object,
        default: () => ({}),
      },

      dataLabels: {
        type: Object,
        default: () => ({}),
      },

      showYAxis: {
        type: Boolean,
        default: false,
      },

      mirrorYAxis: {
        type: Boolean,
        default: false,
      },

      columnMin: {
        type: Number,
        default: undefined,
      },

      columnMax: {
        type: Number,
        default: undefined,
      },

      lineMin: {
        type: Number,
        default: undefined,
      },

      lineMax: {
        type: Number,
        default: undefined,
      },

      titleConfig: {
        type: Object,
        default: () => ({}),
      },

      minWidth: {
        type: Number,
      },

      legendTextMaxWidth: {
        type: Number,
      },

      tooltipMaxWidth: {
        type: Number,
      },

      emptyMessage: {
        type: String,
      },

      enableZoom: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        tooltipPositioning: createTooltipPositioning(),
      };
    },
    computed: {
      getChartHeight() {
        return this.height || 450;
      },
      getZoomXAxisIndex() {
        return this.getUseSplitLayout ? [0, 1] : 0;
      },
      getNormalizedColumnData() {
        return this.columnYData.map((value) => (value === null || value === undefined ? null : Number(value)));
      },
      getNormalizedLineData() {
        return this.lineYData.map((value) => (value === null || value === undefined ? null : Number(value)));
      },
      getResolvedColumns() {
        const normalizeData = (data = []) =>
          data.map((value) => (value === null || value === undefined ? null : Number(value)));

        if (Array.isArray(this.columns)) {
          return this.columns.map((column, index) => ({
            name: column?.name ?? column?.label ?? '',
            data: normalizeData(column?.data),
            color: column?.color ?? this.colors[index] ?? this.colors[0],
            dataLabelsText: column?.dataLabelsText ?? this.columnDataLabelsText,
          }));
        }

        return [
          {
            name: this.columnYName,
            data: this.getNormalizedColumnData,
            color: this.colors[0],
            dataLabelsText: this.columnDataLabelsText,
          },
        ];
      },
      getResolvedLines() {
        const normalizeData = (data = []) =>
          data.map((value) => (value === null || value === undefined ? null : Number(value)));

        if (Array.isArray(this.lines)) {
          return this.lines.map((line, index) => ({
            name: normalizeEChartsLabel(line?.name ?? line?.label ?? ''),
            data: normalizeData(line?.data),
            color: line?.color ?? this.colors[this.getResolvedColumns.length + index] ?? this.colors[1],
            tooltipPrefix: line?.tooltipPrefix,
            dataLabelsText: line?.dataLabelsText ?? this.lineDataLabelsText,
          }));
        }

        return [
          {
            name: normalizeEChartsLabel(this.lineYName),
            data: this.getNormalizedLineData,
            color: this.colors[1],
            tooltipPrefix: this.lineYTooltipPrefix,
            dataLabelsText: this.lineDataLabelsText,
          },
        ];
      },
      getIsChartDataEmpty() {
        if (!this.xData.length) return true;
        const hasFiniteNumber = (value) =>
          value !== null && value !== undefined && typeof value === 'number' && Number.isFinite(value);
        const hasColumnPoint = this.getResolvedColumns.some((column) => column.data.some(hasFiniteNumber));
        const hasLinePoint = this.getResolvedLines.some((line) => line.data.some(hasFiniteNumber));
        return !hasColumnPoint && !hasLinePoint;
      },
      getUseSplitLayout() {
        return this.layout === 'split' && this.getResolvedLines.length > 0;
      },
      getChartOptions() {
        const vue = this;
        const resolvedColumns = this.getResolvedColumns;
        const resolvedLines = this.getResolvedLines;
        const useSplitLayout = this.getUseSplitLayout;
        const barWidth = this.plotOptions?.bar?.columnWidth;
        const gridLeft = this.showYAxis || !useSplitLayout ? 70 : 40;

        const formatAxisValue = (value) => {
          if (vue.formatYLabelFunction) return vue.formatYLabelFunction(value);
          return vue.formatNumber(value, 1, '', ',', true);
        };

        const categoryAxisLabel = {
          color: '#989898',
          fontSize: this.sizeXYLabel,
          fontFamily: 'Fira Sans',
          margin: 16,
          hideOverlap: !useSplitLayout,
        };

        const { grid, xAxis, yAxis, columnAxisIndex, lineAxisIndex } = useSplitLayout
          ? this.getSplitAxesOptions({
              gridLeft,
              categoryAxisLabel,
              formatAxisValue,
            })
          : this.getCombinedAxesOptions({
              gridLeft,
              categoryAxisLabel,
              formatAxisValue,
            });

        const columnSeriesList = resolvedColumns.map((column, columnIndex) => ({
          name: normalizeEChartsLabel(column.name),
          type: 'bar',
          stack: resolvedColumns.length > 1 ? 'columns' : undefined,
          xAxisIndex: columnAxisIndex,
          yAxisIndex: columnAxisIndex,
          data: column.data,
          barWidth,
          barMaxWidth: this.barMaxWidth,
          itemStyle: {
            color: column.color,
          },
          label: {
            show: this.dataLabels?.enabledOnSeries?.includes(columnIndex) ?? true,
            position: 'top',
            distance: Math.min(8, Math.max(2, Math.abs(this.offsetYDataLabel))),
            color: column.color,
            fontSize: this.sizeDataLabel,
            fontFamily: 'Fira Sans',
            formatter: ({ value, dataIndex }) => {
              if (value === null || value === undefined) return '';
              if (this.dataLabels?.formatter) {
                return this.dataLabels.formatter(value, { seriesIndex: columnIndex, dataPointIndex: dataIndex });
              }
              if (this.formatYLabelFunction) return this.formatYLabelFunction(value);
              const formattedVal = this.formatNumber(value, 1, '', ',', true);
              const suffix = column.dataLabelsText ? ` ${column.dataLabelsText}` : '';
              return `${formattedVal}${suffix}`;
            },
          },
        }));

        const lineSeriesList = resolvedLines.map((line, lineIndex) => {
          const logicalSeriesIndex = resolvedColumns.length + lineIndex;
          const strokeWidth = Array.isArray(this.stroke?.width)
            ? this.stroke.width[logicalSeriesIndex] ?? this.stroke.width[1] ?? 4
            : this.stroke?.width ?? 4;
          const dashArray = Array.isArray(this.stroke?.dashArray)
            ? this.stroke.dashArray[logicalSeriesIndex] ?? this.stroke.dashArray[1]
            : this.stroke?.dashArray;
          const markerSize = this.marker?.size ?? (useSplitLayout ? 6 : 4);

          return {
            name: line.name,
            type: 'line',
            xAxisIndex: lineAxisIndex,
            yAxisIndex: lineAxisIndex,
            clip: false,
            data: line.data,
            connectNulls: false,
            symbol: 'circle',
            symbolSize: markerSize,
            showSymbol: markerSize > 0,
            itemStyle: {
              color: line.color,
              borderColor: line.color,
              borderWidth: this.marker?.strokeWidth ?? 2,
            },
            lineStyle: {
              color: line.color,
              width: strokeWidth,
              type: dashArray ? 'dashed' : 'solid',
            },
            z: 10 + lineIndex,
            label: {
              show: this.dataLabels?.enabledOnSeries?.includes(logicalSeriesIndex) ?? true,
              position: 'top',
              distance: 8,
              color: this.dataLabels?.style?.colors?.[logicalSeriesIndex] || line.color,
              fontSize: this.sizeDataLabel,
              fontFamily: 'Fira Sans',
              formatter: ({ value, dataIndex }) => {
                if (value === null || value === undefined) return '';
                if (this.dataLabels?.formatter) {
                  return this.dataLabels.formatter(value, {
                    seriesIndex: logicalSeriesIndex,
                    dataPointIndex: dataIndex,
                  });
                }
                if (this.formatYLabelFunction) return this.formatYLabelFunction(value);
                const formattedVal = this.formatNumber(value, 1, '', ',', true);
                const suffix = line.dataLabelsText ? ` ${line.dataLabelsText}` : '';
                return `${formattedVal}${suffix}`;
              },
            },
          };
        });

        const series = useSplitLayout
          ? [...lineSeriesList, ...columnSeriesList]
          : [...columnSeriesList, ...lineSeriesList];

        const chartOptions = {
          animation: true,
          axisPointer: {
            link: [
              {
                xAxisIndex: 'all',
              },
            ],
          },
          legend: {
            show: true,
            icon: 'circle',
            orient: 'horizontal',
            left: 'center',
            bottom: 8,
            itemWidth: 12,
            itemHeight: 12,
            padding: [20, 0, 0, 0],
            itemGap: 24,
            textStyle: {
              color: '#304758',
              fontFamily: 'Fira Sans',
              fontSize: 14,
              ...(this.legendTextMaxWidth > 0
                ? {
                    width: this.legendTextMaxWidth,
                    overflow: 'truncate',
                    ellipsis: '...',
                  }
                : {}),
            },
          },
          tooltip: {
            trigger: 'axis',
            appendTo: this.tooltipPositioning.appendTo,
            position: this.tooltipPositioning.position,
            axisPointer: {
              type: 'line',
            },
            padding: 0,
            ...(this.tooltipMaxWidth > 0
              ? {
                  extraCssText: `max-width:${this.tooltipMaxWidth}px;white-space:normal;word-break:break-word;`,
                }
              : {}),
            formatter: (params) => {
              return buildCardAxisTooltipHtml(params, {
                getHeader: (listParams) => {
                  const firstParam = listParams[0];
                  if (!firstParam) return '';

                  const index = firstParam.dataIndex ?? 0;
                  if (Array.isArray(this.tooltipLabels) && this.tooltipLabels[index] != null) {
                    return this.tooltipLabels[index];
                  }
                  return this.xData[index] ?? '';
                },
                getSeriesLabel: (param) => {
                  const seriesIndex = param.seriesIndex ?? 0;

                  if (useSplitLayout) {
                    if (seriesIndex < resolvedLines.length) {
                      const line = resolvedLines[seriesIndex];
                      const prefix = line.tooltipPrefix ? ` ${line.tooltipPrefix}` : '';
                      return `${line.name}${prefix}`;
                    }
                    const column = resolvedColumns[seriesIndex - resolvedLines.length];
                    return normalizeEChartsLabel(column?.name);
                  }

                  if (seriesIndex < resolvedColumns.length) {
                    return normalizeEChartsLabel(resolvedColumns[seriesIndex]?.name);
                  }
                  const line = resolvedLines[seriesIndex - resolvedColumns.length];
                  if (!line) return '';
                  const prefix = line.tooltipPrefix ? ` ${line.tooltipPrefix}` : '';
                  return `${line.name}${prefix}`;
                },
                formatValue: (param) => {
                  const index = param.dataIndex ?? 0;
                  if (this.formatTooltipFunction) {
                    return this.formatTooltipFunction(param.value, {
                      seriesIndex: param.seriesIndex,
                      dataPointIndex: index,
                    });
                  }
                  return this.formatNumber(param.value, 1, '', ',', true);
                },
              });
            },
          },
          grid,
          xAxis,
          yAxis,
          series,
        };

        return chartOptions;
      },
    },
    methods: {
      getChartInstance() {
        const vChart = this.chartRef;
        return vChart?.chart?.value ?? vChart?.chart ?? null;
      },
      onChartFinished(bindVChart) {
        bindVChart?.(this.chartRef);
        if (this._skipMirrorSyncOnce) {
          this._skipMirrorSyncOnce = false;
          return;
        }
        this.syncMirrorYAxisFromLeft();
      },
      onLegendSelectChanged() {
        if (!this.mirrorYAxis) return;
        this.$nextTick(() => {
          requestAnimationFrame(() => this.syncMirrorYAxisFromLeft());
        });
      },
      syncMirrorYAxisFromLeft() {
        if (!this.mirrorYAxis || this.getUseSplitLayout) return;

        const chart = this.getChartInstance();
        if (!chart || typeof chart.getModel !== 'function' || typeof chart.setOption !== 'function') return;

        const leftScale = chart.getModel().getComponent('yAxis', 0)?.axis?.scale;
        if (!leftScale || typeof leftScale.getExtent !== 'function') return;

        const [min, max] = leftScale.getExtent();
        if (!Number.isFinite(min) || !Number.isFinite(max)) return;

        const interval = typeof leftScale.getInterval === 'function' ? leftScale.getInterval() : undefined;

        const rightScale = chart.getModel().getComponent('yAxis', 1)?.axis?.scale;
        if (rightScale && typeof rightScale.getExtent === 'function') {
          const [rightMin, rightMax] = rightScale.getExtent();
          const rightInterval = typeof rightScale.getInterval === 'function' ? rightScale.getInterval() : undefined;
          if (rightMin === min && rightMax === max && rightInterval === interval) return;
        }

        this._skipMirrorSyncOnce = true;
        chart.setOption({
          yAxis: [
            {},
            {
              min,
              max,
              ...(interval != null ? { interval } : {}),
            },
          ],
        });
      },
      getCombinedAxesOptions({ categoryAxisLabel, formatAxisValue }) {
        const axisLabelStyle = {
          color: categoryAxisLabel.color,
          fontSize: categoryAxisLabel.fontSize,
          fontFamily: categoryAxisLabel.fontFamily,
          formatter: formatAxisValue,
        };

        const useMirror = this.mirrorYAxis;

        const leftYAxis = {
          type: 'value',
          min: this.columnMin,
          max: this.columnMax,
          splitNumber: this.tickAmount,
          interval: this.stepSize,
          position: 'left',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: true, lineStyle: { color: 'rgba(120, 120, 120, 0.1)' } },
          axisLabel: {
            show: this.showYAxis || this.layout === 'combined',
            ...axisLabelStyle,
          },
        };

        const yAxis = useMirror
          ? [
              leftYAxis,
              {
                type: 'value',
                position: 'right',
                axisLine: { show: false },
                axisTick: { show: false },
                splitLine: { show: false },
                axisLabel: {
                  ...axisLabelStyle,
                  show: true,
                },
              },
            ]
          : leftYAxis;

        return {
          grid: {
            left: '3%',
            right: useMirror ? 56 : '4%',
            top: this.enableZoom ? ECHARTS_CHART_ZOOM_GRID_TOP : 40,
            bottom: this.gridBottom,
            containLabel: true,
          },
          xAxis: {
            type: 'category',
            data: this.xData,
            boundaryGap: true,
            axisTick: { show: false },
            axisLine: { show: false },
            axisLabel: categoryAxisLabel,
          },
          yAxis,
          columnAxisIndex: 0,
          lineAxisIndex: 0,
        };
      },
      getSplitAxesOptions({ gridLeft, categoryAxisLabel, formatAxisValue }) {
        return {
          grid: [
            {
              left: gridLeft,
              right: 70,
              top: 30,
              height: '24%',
            },
            {
              left: gridLeft,
              right: 70,
              top: '40%',
              bottom: this.gridBottom,
            },
          ],
          xAxis: [
            {
              type: 'category',
              gridIndex: 0,
              data: this.xData,
              axisLabel: { show: false },
              axisTick: { show: false },
              axisLine: { show: false },
            },
            {
              type: 'category',
              gridIndex: 1,
              data: this.xData,
              boundaryGap: true,
              axisTick: { show: false },
              axisLine: { show: false },
              axisLabel: categoryAxisLabel,
            },
          ],
          yAxis: [
            {
              type: 'value',
              gridIndex: 0,
              min: this.lineMin,
              max: this.lineMax,
              splitNumber: this.tickAmountSecond,
              interval: this.stepSizeSecond,
              axisLine: { show: false },
              axisTick: { show: false },
              splitLine: { show: false },
              axisLabel: {
                show: false,
                color: categoryAxisLabel.color,
                fontSize: categoryAxisLabel.fontSize,
                fontFamily: categoryAxisLabel.fontFamily,
                formatter: formatAxisValue,
              },
            },
            {
              type: 'value',
              gridIndex: 1,
              min: this.columnMin,
              max: this.columnMax,
              splitNumber: this.tickAmount,
              interval: this.stepSize,
              axisLine: { show: false },
              axisTick: { show: false },
              splitLine: { show: false },
              axisLabel: {
                show: this.showYAxis,
                color: categoryAxisLabel.color,
                fontSize: categoryAxisLabel.fontSize,
                fontFamily: categoryAxisLabel.fontFamily,
                formatter: formatAxisValue,
              },
            },
          ],
          columnAxisIndex: 1,
          lineAxisIndex: 0,
        };
      },
    },
  };
</script>

<style lang="scss" scoped>
  .chartBar {
    width: 100%;
  }
</style>
